import { useState, type KeyboardEvent } from "react";
import type { SettingSpecEntry, SettingValue } from "@/api";
import { humanChoice, parseInput, toInput } from "@/lib/settings";
import { Input, Select, Switch, Textarea } from "@/ui";

export interface ChoiceOption {
  value: string;
  label: string;
}

export interface SettingControlProps {
  settingKey: string;
  spec: SettingSpecEntry;
  /** Current effective value. Ignored when `mixed`. */
  value: SettingValue | undefined;
  /** Bulk selection disagrees on this key. */
  mixed?: boolean;
  onCommit: (value: SettingValue) => void;
  disabled?: boolean;
  labelledBy?: string;
  describedBy?: string;
  /** Replaces spec choices / turns a str field into a picker (e.g. alert channel). */
  options?: ChoiceOption[];
}

/**
 * The editor for one setting, chosen from its spec type. Booleans and choices commit on
 * change; text, number and list fields commit on blur or Enter (Ctrl+Enter for lists).
 */
export function SettingControl(props: SettingControlProps) {
  const { spec, value, mixed, onCommit, disabled, labelledBy, describedBy, settingKey } = props;
  const aria = { "aria-labelledby": labelledBy, "aria-describedby": describedBy };

  if (spec.type === "bool") {
    return (
      <Switch
        {...aria}
        checked={mixed ? "mixed" : Boolean(value)}
        onChange={(next) => onCommit(next)}
        disabled={disabled}
        data-setting={settingKey}
      />
    );
  }

  const choices: ChoiceOption[] | null =
    props.options ??
    (spec.type === "choice" ? spec.choices.map((c) => ({ value: c, label: humanChoice(c) })) : null);

  if (choices) {
    const current = mixed ? "" : String(value ?? "");
    const known = choices.some((c) => c.value === current);
    const opts = [
      ...(mixed ? [{ value: "", label: "Mixed values", disabled: true }] : []),
      ...(!mixed && !known ? [{ value: current, label: current || "(not set)" }] : []),
      ...choices,
    ];
    return (
      <Select
        {...aria}
        size="sm"
        value={current}
        options={opts}
        disabled={disabled}
        data-setting={settingKey}
        onChange={(e) => onCommit(e.target.value)}
      />
    );
  }

  // Text-like: remount on external value change so the draft resets.
  return <TextControl key={mixed ? "\u0000mixed" : JSON.stringify(value)} {...props} />;
}

function TextControl({
  spec,
  value,
  mixed,
  onCommit,
  disabled,
  labelledBy,
  describedBy,
  settingKey,
}: SettingControlProps) {
  const initial = mixed ? "" : toInput(value);
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | undefined>();

  const commit = () => {
    if (draft === initial) {
      setError(undefined);
      return;
    }
    try {
      const parsed = parseInput(spec, draft);
      setError(undefined);
      onCommit(parsed);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const common = {
    "aria-labelledby": labelledBy,
    "aria-describedby": describedBy,
    "data-setting": settingKey,
    disabled,
    error,
    placeholder: mixed ? "Mixed values" : spec.type === "list" ? "One per line" : undefined,
    onBlur: commit,
  };

  if (spec.type === "list") {
    return (
      <Textarea
        {...common}
        rows={3}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commit();
        }}
      />
    );
  }
  return (
    <Input
      {...common}
      size="sm"
      type={spec.type === "int" ? "number" : spec.type === "url" ? "url" : "text"}
      inputMode={spec.type === "int" ? "numeric" : undefined}
      min={spec.min ?? undefined}
      max={spec.max ?? undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") {
          setDraft(initial);
          setError(undefined);
        }
      }}
    />
  );
}

